import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Table, Tooltip } from 'antd';
import {
  EyeOutlined,
  WarningOutlined,
  SafetyOutlined,
  DeploymentUnitOutlined,
  InfoCircleOutlined,
  DownOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import { getCustomers } from '../../../../redux/selectors/framl/landing';
import {
  fetchCustomerAlerts,
  fetchQueue,
  setExpandedRowKeys,
  setPage,
  setSize
} from '../../../../redux/actions/framl/landing';
import {
  hexToRgba,
  resolveLevelColor,
  resolveLevelLabel
} from '../../../cm/utils/framlSummary';
import FramlUnderlyingAlerts from './FramlUnderlyingAlerts';

// ── FIN-11360 mock-parity cell styling ──────────────────────────────────────
const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  background: ${(p) => p.bg};
  color: ${(p) => p.fg};
  border: 1px solid ${(p) => p.bd || 'transparent'};
`;

const DOMAIN_TONE = {
  Fraud: {
    bg: '#fde8e6',
    fg: '#d6453a',
    bd: '#f6cdc8',
    icon: <WarningOutlined />
  },
  AML: {
    bg: '#e3edff',
    fg: '#2f6df6',
    bd: '#cfe0ff',
    icon: <SafetyOutlined />
  },
  'Cross-Domain': {
    bg: '#efe9ff',
    fg: '#7c4dff',
    bd: '#ddd0ff',
    icon: <DeploymentUnitOutlined />
  }
};
function domainPill(domain) {
  const t = DOMAIN_TONE[domain] || { bg: '#eef0f4', fg: '#6b7280', icon: null };
  return (
    <Pill bg={t.bg} fg={t.fg} bd={t.bd}>
      {t.icon}
      {domain}
    </Pill>
  );
}

function makeRiskPillRenderer(alertLevels) {
  // Label AND colour come from CM Settings (cmWorkFlowStatus.alertLevels), through the same
  // canonical helpers the Case Summary tab uses, so a renamed/recoloured level reads the same
  // everywhere. FIN-13941 — the colour used to come from a hardcoded LOW/GOOD/HIGH palette, so
  // e.g. a level configured blue (#1273DE) still rendered red. Background/border are alpha tints
  // of the configured colour (LiveCustomerRisk's band banner recipe); an unconfigured level gets
  // resolveLevelColor's neutral grey.
  return function riskPill(code, bandLabel) {
    const label = resolveLevelLabel(alertLevels, bandLabel || code) || code;
    const fg = resolveLevelColor(alertLevels, code || bandLabel);
    return (
      <Pill
        bg={hexToRgba(fg, 0.1, '#eef0f4')}
        fg={fg}
        bd={hexToRgba(fg, 0.25, 'transparent')}>
        {label}
      </Pill>
    );
  };
}

// score + colour bar. FRAML Score passes its row's configured Alert Level colour; Fraud Heat
// and AML Score still fall back to barColor's fixed bands.
const ScoreWrap = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 56px;
`;
const ScoreNum = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1f2430;
  font-variant-numeric: tabular-nums;
`;
const Track = styled.div`
  width: 100%;
  height: 5px;
  border-radius: 3px;
  background: #eef0f4;
  overflow: hidden;
`;
const Fill = styled.div`
  height: 100%;
  width: ${(p) => p.pct}%;
  background: ${(p) => p.color};
  border-radius: 3px;
`;
// ponytail: FIN-14140 — still drives Fraud Heat and AML Score, whose colour source awaits a
// product ruling (Fraud Heat) and a defined IAD source (AML Score). Delete once both land.
function barColor(v) {
  if (v >= 70) return '#e0483b';
  if (v >= 40) return '#e29840';
  return '#3fb56e';
}
// FIN-12421 — displayed with 2dp precision to avoid raw doubles like 17.28323157332234.
// The pill-fill percentage still uses the raw value so bar length stays continuous.
function scoreBar(value, color) {
  if (value == null || value === 0)
    return <span style={{ color: '#9aa0aa' }}>—</span>;
  const num = Number(value);
  const pct = Math.max(4, Math.min(100, num));
  return (
    <ScoreWrap>
      <ScoreNum>{num.toFixed(2)}</ScoreNum>
      <Track>
        <Fill pct={pct} color={color || barColor(num)} />
      </Track>
    </ScoreWrap>
  );
}

const ASSIGN_TONE = {
  UNASSIGNED: { bg: '#eef0f4', fg: '#6b7280', bd: '#dfe3ea' },
  ASSIGNED: { bg: '#e3edff', fg: '#2f6df6', bd: '#cfe0ff' },
  PARTIALLY_ASSIGNED: { bg: '#fdf2e0', fg: '#b9770e', bd: '#f0d9a8' }
};
const AssignWrap = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;
const AssignCount = styled.span`
  font-size: 11px;
  color: #8a909c;
  font-variant-numeric: tabular-nums;
`;
function prettyAssign(s) {
  return (s || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function assignmentRender(_, record) {
  const code = (record.assignmentStatus || 'UNASSIGNED').toUpperCase();
  const t = ASSIGN_TONE[code] || ASSIGN_TONE.UNASSIGNED;
  return (
    <AssignWrap>
      <Pill bg={t.bg} fg={t.fg} bd={t.bd}>
        {prettyAssign(record.assignmentStatus)}
      </Pill>
      <AssignCount>
        {record.assignedCount}/{record.totalAlerts}
      </AssignCount>
    </AssignWrap>
  );
}

const EyeLink = styled(Link)`
  color: #6b7280;
  font-size: 16px;
  &:hover {
    color: #2f6df6;
  }
`;
const Dash = () => <span style={{ color: '#9aa0aa' }}>—</span>;
const Count = styled.span`
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.color || '#1f2430'};
`;
// FIN-13792: count-column header — icon + label on one line, same shape as
// the summary-card labels (FramlHeaderStats).
const CountHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

function buildColumns(alertLevels) {
  const riskPill = makeRiskPillRenderer(alertLevels);
  return [
    // Mock parity: regular weight (no bold) — matches the Customer column.
    {
      title: 'Customer ID',
      dataIndex: 'customerKey',
      key: 'customerKey',
      width: 132,
      // FIN-12889/12910 — long party keys overflowed with no own hover (the visible
      // truncated-hover came from a neighbouring cell's title). Ellipsis + a real
      // Tooltip carrying the full key.
      ellipsis: true,
      render: (key) => (
        <Tooltip title={key}>
          <span data-testid={`customer-id-${key}`}>{key}</span>
        </Tooltip>
      )
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 96,
      ellipsis: true
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      width: 132,
      render: (v) => domainPill(v)
    },
    {
      title: 'FRAML Score',
      dataIndex: 'framlScore',
      key: 'framlScore',
      align: 'center',
      width: 78,
      // FIN-14140 — same colour as the Alert Level pill on this row (same resolver, same key),
      // so the two columns can never disagree. barColor's 70/40 split painted every queued
      // customer red, because the suppression floor is 70.
      render: (v, record) =>
        scoreBar(
          v,
          resolveLevelColor(
            alertLevels,
            record.riskBand || record.riskBandLabel
          )
        )
    },
    {
      // FIN-12916 — PRD 'FRAML Prevention - Case Manager FRAML View' (page 3214147650) names
      // this ALERT LEVEL in every requirement section: the UI reference filter list, the §3
      // column spec and the §8 filter spec, values Low / Good / High. 'Risk' / 'risk band'
      // appears only in the §5 heading and the acceptance-criteria prose - and §5's own bullet
      // then says 'the alert level should be shown as a pill', contradicting its own heading.
      // The requirement sections win; the AC wording is a PRD correction, not a code change.
      title: 'Alert Level',
      dataIndex: 'riskBand',
      key: 'riskBand',
      align: 'center',
      width: 76,
      render: (v, record) => riskPill(v, record.riskBandLabel)
    },
    {
      title: 'Fraud Heat',
      dataIndex: 'fraudHeat',
      key: 'fraudHeat',
      align: 'center',
      width: 94,
      // Wrapped: antd passes the row as arg 2, which scoreBar would read as the colour.
      render: (v) => scoreBar(v)
    },
    {
      title: (
        <span>
          AML Score{' '}
          <Tooltip title="latest TM alert score for this customer">
            <InfoCircleOutlined style={{ color: '#b0b6c0', fontSize: 11 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'amlTm',
      key: 'amlTm',
      align: 'center',
      width: 94,
      render: (v) => (v == null ? <Dash /> : scoreBar(v))
    },
    // FIN-13792: icon + text label, like every other column (was icon-only,
    // FIN-11360 D20). Wording matches the summary cards (FramlHeaderStats);
    // the Tooltip keeps the full "… alert count" meaning.
    {
      title: (
        <Tooltip title="Fraud alert count">
          <CountHeader>
            <WarningOutlined style={{ color: '#e0483b' }} />
            Fraud
          </CountHeader>
        </Tooltip>
      ),
      dataIndex: 'fraudAlertCount',
      key: 'fraudAlertCount',
      align: 'center',
      width: 72,
      render: (v) => <Count color="#e0483b">{v}</Count>
    },
    {
      title: (
        <Tooltip title="AML alert count">
          <CountHeader>
            <SafetyOutlined style={{ color: '#2f6df6' }} />
            AML
          </CountHeader>
        </Tooltip>
      ),
      dataIndex: 'amlAlertCount',
      key: 'amlAlertCount',
      align: 'center',
      width: 72,
      render: (v) => <Count color="#2f6df6">{v}</Count>
    },
    {
      title: 'Date',
      dataIndex: 'latestAlertDate',
      key: 'latestAlertDate',
      width: 96,
      // Date-only (mock shows YYYY-MM-DD, not the full ISO timestamp) — also
      // keeps the column narrow so the grid fits without horizontal scroll.
      render: (v) => (
        <span style={{ color: '#6b7280', fontSize: 12 }}>
          {v ? String(v).slice(0, 10) : '—'}
        </span>
      )
    },
    {
      title: 'Assignment',
      key: 'assignment',
      align: 'center',
      width: 130,
      render: assignmentRender
    },
    {
      title: 'View',
      key: 'view',
      align: 'center',
      width: 50,
      render: (_, record) => (
        // FIN-11360: opens the customer-level FRAML detail (FIN-11361). Clean
        // customer-keyed path; stopPropagation so the eye doesn't also toggle the
        // row-click expand. Route lands with FIN-11361.
        <EyeLink
          to={`/app/cm/framl/customer/${record.customerKey}`}
          onClick={(e) => e.stopPropagation()}>
          <EyeOutlined />
        </EyeLink>
      )
    }
  ];
}

// Sizing measured off the mock: th 11px/500, td 14px/#29303d, row ~81px.
// nowrap on headers + scroll={{ x }} on the Table keeps long labels/cells on one
// line and lets the grid scroll horizontally instead of wrapping into the next row.
const TableWrap = styled.div`
  /* Contain any residual width to the table (never scroll the whole page);
     after date-only + no expander column the grid fits without a scrollbar. */
  overflow-x: auto;
  .ant-table {
    font-size: 14px;
  }
  /* compact horizontal padding (8px) so all 13 columns fit without h-scroll */
  .ant-table-thead > tr > th {
    background: #f7f8fb;
    color: #676f7e;
    font-weight: 500;
    font-size: 11px;
    line-height: 1.3;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    /* allow multi-word headers (FRAML Score, Alert Level…) to wrap to two
       lines like the mock instead of clipping in the narrow score columns */
    white-space: normal;
    padding: 10px 8px;
    border-bottom: 1px solid #e8eaf0;
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f1f5;
    padding: 16px 8px;
    color: #29303d;
    white-space: nowrap;
  }
  .ant-table-tbody > tr:hover > td {
    background: #f9fafe;
  }
`;

function FramlCustomerTable({
  customers,
  customerAlerts,
  customerAlertsMeta,
  // FIN-13005 — customerKey -> error message when that row's expand fetch failed.
  customerAlertsError,
  searchText,
  fetchCustomerAlerts: doFetch,
  fetchQueue: doFetchQueue,
  setPage: doSetPage,
  setSize: doSetSize,
  setExpandedRowKeys: doSetExpandedRowKeys,
  expandedRowKeys,
  pagination,
  selectedRowKeys,
  onSelectChange,
  alertLevels,
  workFlowStatusList
}) {
  const columns = buildColumns(alertLevels);

  const { page = 0, size = 10, total = 0 } = pagination || {};

  const handlePageChange = (newPage) => {
    // antd pages are 1-based; store is 0-based
    doSetPage(newPage - 1);
    doFetchQueue();
  };

  const handleSizeChange = (_current, newSize) => {
    doSetPage(0);
    doSetSize(newSize);
    doFetchQueue();
  };

  // FIN-12887: expand consumes the server-paged envelope — meta drives the pager,
  // page changes refetch that customer's page. FIN-12919: the queue's active search
  // text is threaded down so matching typology rows highlight.
  const expandedRowRender = (record) => {
    const meta = (customerAlertsMeta || {})[record.customerKey] || {};
    return (
      <FramlUnderlyingAlerts
        alerts={customerAlerts[record.customerKey] || []}
        total={meta.total}
        page={meta.page}
        size={meta.size}
        onPageChange={(page, size) => doFetch(record.customerKey, page, size)}
        workFlowStatusList={workFlowStatusList}
        highlight={searchText}
        error={(customerAlertsError || {})[record.customerKey]}
        // FIN-13008 — retry what was on screen, not the default page size.
        onRetry={() => doFetch(record.customerKey, meta.page, meta.size)}
      />
    );
  };
  const onExpand = (expanded, record) => {
    // FIN-13008 — keep the user's chosen page size across collapse/re-expand.
    if (expanded)
      doFetch(
        record.customerKey,
        0,
        ((customerAlertsMeta || {})[record.customerKey] || {}).size
      );
  };

  // FIN-13938 — expanded rows are mirrored into the landing slice so they survive leaving the page
  // (Alert ID → case page → Back). Local state drives the table; it starts from the slice and follows
  // it when the slice changes underneath (a filter/sort change collapses everything).
  const [expandedKeys, setExpandedKeys] = useState(expandedRowKeys);
  useEffect(() => {
    setExpandedKeys(expandedRowKeys);
  }, [expandedRowKeys]);
  const onExpandedRowsChange = (keys) => {
    setExpandedKeys(keys);
    doSetExpandedRowKeys(keys);
  };

  // FIN-13938 — an expanded row's alerts are refetched whenever that row BECOMES VISIBLE: on mount
  // (back from the case page), when the queue refetch brings it onto the page, and when paging back
  // to it. The first render after a return uses the customers cached in the slice and the page's own
  // queue fetch then replaces them, so a mount-only refetch missed rows that only appear after it
  // (review R5). Rows that stay on screen across an auto-refresh are not refetched (unchanged). The
  // row keeps its own inner page (review L3), not page 1.
  const prevOnPageRef = useRef(new Set());
  useEffect(() => {
    const onPage = new Set((customers || []).map((c) => c.customerKey));
    (expandedKeys || [])
      .filter((key) => onPage.has(key) && !prevOnPageRef.current.has(key))
      .forEach((key) => {
        const meta = (customerAlertsMeta || {})[key] || {};
        doFetch(key, meta.page ?? 0, meta.size ?? 20);
      });
    prevOnPageRef.current = onPage;
  }, [customers]);

  return (
    <TableWrap>
      <Table
        rowKey="customerKey"
        dataSource={customers}
        columns={columns}
        pagination={{
          current: page + 1,
          pageSize: size,
          total,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
          showTotal: (t, r) => `Showing ${r[0]}-${r[1]} of ${t}`,
          onChange: handlePageChange,
          onShowSizeChange: handleSizeChange
        }}
        expandable={{
          expandedRowRender,
          onExpand,
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange,
          // FIN-11360: rows still expand on click, but a rotating chevron gives the
          // affordance (demo feedback: the click-to-expand row had no visible cue).
          // stopPropagation on the chevron avoids a double-toggle with expandRowByClick.
          expandRowByClick: true,
          expandIcon: ({ expanded, onExpand: toggle, record }) => (
            <DownOutlined
              aria-label={`Expand ${record.customerKey}`}
              onClick={(e) => {
                e.stopPropagation();
                toggle(record, e);
              }}
              style={{
                transition: 'transform .15s',
                transform: expanded ? 'rotate(180deg)' : 'none',
                cursor: 'pointer',
                color: '#8a909c'
              }}
            />
          )
        }}
        rowSelection={{ selectedRowKeys, onChange: onSelectChange }}
      />
    </TableWrap>
  );
}

FramlCustomerTable.propTypes = {
  customers: PropTypes.array,
  customerAlerts: PropTypes.object,
  customerAlertsMeta: PropTypes.object,
  customerAlertsError: PropTypes.object,
  searchText: PropTypes.string,
  fetchCustomerAlerts: PropTypes.func.isRequired,
  fetchQueue: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSize: PropTypes.func.isRequired,
  setExpandedRowKeys: PropTypes.func.isRequired,
  expandedRowKeys: PropTypes.array,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    total: PropTypes.number
  }),
  selectedRowKeys: PropTypes.array,
  onSelectChange: PropTypes.func,
  alertLevels: PropTypes.array,
  workFlowStatusList: PropTypes.array
};

// Stable fallback so the slice→local sync effect does not re-fire on every render.
const NO_EXPANDED_ROWS = [];

FramlCustomerTable.defaultProps = {
  customers: [],
  customerAlerts: {},
  customerAlertsMeta: {},
  customerAlertsError: {},
  searchText: '',
  expandedRowKeys: NO_EXPANDED_ROWS,
  pagination: { page: 0, size: 10, total: 0 },
  selectedRowKeys: [],
  onSelectChange: () => {},
  alertLevels: undefined,
  workFlowStatusList: []
};

function mapStateToProps(state) {
  return {
    customers: getCustomers(state),
    customerAlerts: state.framlLanding.customerAlerts,
    customerAlertsMeta: state.framlLanding.customerAlertsMeta,
    // FIN-13005 — per-row expand failure, so a failed fetch doesn't read as "no alerts".
    customerAlertsError: state.framlLanding.customerAlertsError,
    // FIN-12919: active queue search — expand rows with a matching typology highlight.
    searchText: state.framlLanding.filters?.search || '',
    pagination: state.framlLanding.pagination,
    expandedRowKeys: state.framlLanding.expandedRowKeys || NO_EXPANDED_ROWS,
    // FIN-11360: same store the Case Summary tab uses (cmWorkFlowStatus) so the
    // configured level displayName shows consistently across CM.
    alertLevels: state.cmWorkFlowStatus?.alertLevels || [],
    workFlowStatusList: state.cmWorkFlowStatus?.workFlowStatusList || []
  };
}

const mapDispatchToProps = {
  fetchCustomerAlerts,
  fetchQueue,
  setExpandedRowKeys,
  setPage,
  setSize
};

export default connect(mapStateToProps, mapDispatchToProps)(FramlCustomerTable);
